import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import OpportunityStage from "@/services/opportunity/opportunity-stage";
import type { GetOpportunityStage } from "@/dtos/OpportunityStage.dto";

export interface OpportunityStagesParams {
  organizationId: string;
  sortBy?: string;
  direction?: string;
  contactId?: string;
  startDate?: string;
  endDate?: string;
  name?: string;
  confidence?: string;
  expectedCloseDate?: string;
  dealType?: string;
  opportunityStageId?: string;
  ownerId?: string | null;
  age?: string;
  totalAmount?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useOpportunityStages(
  params: Ref<OpportunityStagesParams | null> | OpportunityStagesParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["opportunityStages", "disabled"];
    return [
      "opportunityStages",
      p.organizationId,
      p.sortBy || "",
      p.direction || "",
      p.contactId || "",
      p.startDate || "",
      p.endDate || "",
      p.name || "",
      p.confidence || "",
      p.expectedCloseDate || "",
      p.dealType || "",
      p.opportunityStageId || "",
      p.ownerId ?? "all",
      p.age || "",
      p.totalAmount || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetOpportunityStage = {
        organizationId: p.organizationId,
      };

      if (p.sortBy) queryParams.sortBy = p.sortBy;
      if (p.direction) queryParams.direction = p.direction;
      if (p.contactId) queryParams.contactId = p.contactId;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.name) queryParams.name = p.name;
      if (p.confidence) queryParams.confidence = p.confidence;
      if (p.expectedCloseDate) queryParams.expectedCloseDate = p.expectedCloseDate;
      if (p.dealType) queryParams.dealType = p.dealType;
      if (p.opportunityStageId) queryParams.opportunityStageId = p.opportunityStageId;
      if (p.ownerId !== undefined) queryParams.ownerId = p.ownerId;
      if (p.age) queryParams.age = p.age;
      if (p.totalAmount) queryParams.totalAmount = p.totalAmount;

      const result = await OpportunityStage.getOpportunityStages(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load opportunity stages");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
